import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import { LuX } from "react-icons/lu";
import IdealStudio from "../../../../../core/editor/main";
import { isSlide, isContainedView } from "../../../../../common/utils";
import { ID_PREFIX_BOX } from "../../../../../common/constants";
import { randomPositionGenerator } from "../../../clipboard/clipboard.utils";
import { createBox } from "../../../../../common/common_tools";
import getMediaBoxSize from "../../../../utils/getMediaBoxSize";
import AppModal from "../../../common/modal/AppModal";
import PanelSearch from "../ui/PanelSearch";
import AssetGrid from "../ui/AssetGrid";
import AssetCell from "../ui/AssetCell";
import EmptyState from "../ui/EmptyState";
import "./_panel.scss";
import { YOUTUBE_API_KEY as API_KEY } from "../../../../../common/youtube_tools";

function YoutubePanel({
  onBoxAdded,
  onElementSelected,
  fileModalResult,
  boxes,
  navItems,
  navItemSelected,
  containedViews,
  containedViewSelected,
  onClose,
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const search = useCallback(async () => {
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    setSearched(true);
    try {
      const url =
        "https://www.googleapis.com/youtube/v3/search?part=id,snippet&maxResults=24" +
        "&type=video&videoEmbeddable=true&key=" +
        API_KEY +
        "&q=" +
        encodeURIComponent(q);
      const res = await fetch(url);
      const data = await res.json();
      const items = Array.isArray(data.items)
        ? data.items
            .filter((v) => v.id && v.id.videoId)
            .map((v) => {
              const thumbs = (v.snippet && v.snippet.thumbnails) || {};
              const thumb = thumbs.medium || thumbs.default;
              return {
                id: v.id.videoId,
                name: v.snippet ? v.snippet.title : "",
                url: "https://www.youtube.com/embed/" + v.id.videoId,
                thumbnail: thumb ? thumb.url : "",
              };
            })
        : [];
      setResults(items);
    } catch (e) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [query]);

  const addToScene = async (item) => {
    if (!onBoxAdded || !item.url || !boxes || !navItems || !containedViews) return;
    const pluginName = "EnrichedPlayer";
    if (!IdealStudio.Plugins.get(pluginName)) return;

    const navItem =
      containedViewSelected && containedViews[containedViewSelected]
        ? containedViews[containedViewSelected]
        : navItems[navItemSelected];
    if (!navItem) return;

    const cv =
      containedViewSelected &&
      containedViews[containedViewSelected] &&
      isContainedView(containedViewSelected);
    const cvslide = cv && isSlide(navItem.type);
    const inASlide = (!cv && isSlide(navItem.type)) || cvslide;
    const parent = inASlide ? navItem.id : navItem.boxes && navItem.boxes[0];

    const { width, height, widthUnit, heightUnit } = await getMediaBoxSize(item.url, "video");

    const initialParams = {
      parent,
      container: 0,
      name: pluginName,
      position: {
        x: randomPositionGenerator(10, 30),
        y: randomPositionGenerator(10, 30),
        type: "absolute",
      },
      id: ID_PREFIX_BOX + Date.now(),
      page: cv ? containedViewSelected : navItem.id || navItemSelected || 0,
      url: item.url,
      componentType: "Youtube",
      width,
      height,
      widthUnit,
      heightUnit,
    };

    createBox(initialParams, pluginName, inASlide, onBoxAdded, boxes);
  };

  const handleAdd = (item) => {
    if (fileModalResult && fileModalResult.id) {
      if (onElementSelected) onElementSelected(item.name, item.url, "video", undefined);
      return;
    }
    return addToScene(item);
  };

  const renderBody = () => {
    if (loading) return <EmptyState title={i18n.t("searching")} />;
    if (results.length === 0) {
      return searched ? (
        <EmptyState title={i18n.t("no_results")} hint={i18n.t("try_other_search")} />
      ) : (
        <EmptyState title={i18n.t("youtube_search")} hint={i18n.t("youtube_search_hint")} />
      );
    }
    return (
      <AssetGrid columns={2} cellSize={180} gap={10}>
        {results.map((item) => (
          <AssetCell
            key={item.id}
            flush
            ratio={9 / 16}
            label={item.name}
            title={item.name}
            dragProps={{
              className: "palette-item floatingEditorBox",
              name: "EnrichedPlayer",
              "data-plugin": "EnrichedPlayer",
              "data-url": item.url,
              "data-media": "video",
              "data-type": "Youtube",
            }}
            onPreview={() => setPreviewUrl(item.url)}
            onAdd={() => handleAdd(item)}
          >
            {item.thumbnail ? (
              <img
                src={item.thumbnail}
                alt={item.name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : null}
          </AssetCell>
        ))}
      </AssetGrid>
    );
  };

  return (
    <div className="cmp-panel">
      <div className="cmp-panel__toolbar">
        <PanelSearch
          value={query}
          onChange={setQuery}
          onClear={() => setQuery("")}
          onEnter={search}
          placeholder={i18n.t("youtube_search")}
        />
        {onClose && (
          <button
            type="button"
            className="cmp-panel__close"
            onClick={onClose}
            title={i18n.t("close")}
          >
            <LuX size={15} />
          </button>
        )}
      </div>

      <div className="cmp-panel__scroll">{renderBody()}</div>

      {previewUrl && (
        <AppModal
          show
          onClose={() => setPreviewUrl(null)}
          title={i18n.t("preview")}
          centered
          size="lg"
          showSceneToggle={false}
          className="libraryPreviewModal"
        >
          <div className="cmp-panel__video">
            <iframe src={previewUrl} title="YouTube" allowFullScreen />
          </div>
        </AppModal>
      )}
    </div>
  );
}

YoutubePanel.propTypes = {
  onBoxAdded: PropTypes.func,
  onElementSelected: PropTypes.func,
  fileModalResult: PropTypes.object,
  boxes: PropTypes.object,
  navItems: PropTypes.object,
  navItemSelected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  containedViews: PropTypes.object,
  containedViewSelected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func,
};

export default YoutubePanel;
